import React from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useRouteError, Link, isRouteErrorResponse } from "react-router-dom";
import { Box, Container, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

// Router Error Fallback for React Router errors
const FallbackUI: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
   <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: isDarkMode ? "#1b1f24" : "#f8fafc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background orbs */}
      <Box
        sx={{
          position: "absolute",
          width: 240,
          height: 240,
          borderRadius: "50%",
          background:
            "radial-gradient(closest-side, rgba(255,104,68,0.15), transparent)",
          top: -40,
          left: -40,
          filter: "blur(2px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(closest-side, rgba(47,51,140,0.15), transparent)",
          bottom: -60,
          right: -60,
          filter: "blur(2px)",
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative" }}>
        <Box
          sx={{
            bgcolor: isDarkMode ? "#21262c" : "#ffffff",
            borderRadius: 4,
            boxShadow: isDarkMode
              ? "0 10px 30px rgba(0,0,0,0.6)"
              : "0 10px 30px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          {/* Top gradient bar */}
          <Box
            sx={{
              height: 8,
              background:
                "linear-gradient(90deg, #106423 0%, #0c531f 40%, #2F338C 100%)",
            }}
          />

          {/* Content */}
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Icon badge */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                mx: "auto",
                mb: 2,
                display: "grid",
                placeItems: "center",
                background: isDarkMode
                  ? "rgba(255,104,68,0.15)"
                  : "rgba(255,104,68,0.1)",
                border: `1px solid ${
                  isDarkMode ? "rgba(41,61,194,0.35)" : "rgba(41,61,194,0.3)"
                }`,
                color: "#293d90",
                fontWeight: 800,
                fontSize: 28,
              }}
              aria-hidden
            >
              !
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: isDarkMode ? "#ffffff" : "#0F202B",
                mb: 1.5,
                textAlign: "center",
                letterSpacing: -0.2,
                fontSize: { xs: "1.6rem", sm: "1.9rem" },
              }}
            >
              Something went wrong
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: isDarkMode ? "#cfd3da" : "#4b5563",
                mb: 3,
                maxWidth: 520,
                mx: "auto",
                textAlign: "center",
              }}
            >
              We hit an unexpected error. You can reload the page or head back
              to the home screen.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                justifyContent: "center",
              }}
            >
              <Button
                component={Link}
                to="/"
                variant="contained"
                size="large"
                sx={{
                  px: 3,
                  py: 1.4,
                  backgroundColor: "#106423",
                  borderRadius: 30,
                  boxShadow: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#0c531f",
                    boxShadow: 3,
                  },
                }}
              >
                Go to Home
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.location.reload()}
                sx={{
                  px: 3,
                  py: 1.4,
                  borderRadius: 30,
                  textTransform: "none",
                }}
              >
                Reload Page
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export const RouterErrorFallback: React.FC = () => {
  const error = useRouteError();
  // Normalize and log the exact error details to console only
  if (isRouteErrorResponse(error)) {
    const status = error.status;
    const statusText = error.statusText;
    const dataMessage = (error as any)?.data?.message;
    const message = dataMessage || statusText || `Route error ${status}`;
    // eslint-disable-next-line no-console
    console.error("Router error caught:", {
      status,
      statusText,
      message,
      data: (error as any)?.data,
    });
  } else if (error instanceof Error) {
    // eslint-disable-next-line no-console
    console.error("Router error caught:", {
      message: error.message,
      stack: error.stack,
    });
  } else {
    // eslint-disable-next-line no-console
    console.error("Router error caught:", error);
  }
  return <FallbackUI />;
};

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error }) => {
  // Always log to console, but don't show error details in UI
  // eslint-disable-next-line no-console
  console.error("UI error caught:", {
    message: error?.message,
    stack: (error as any)?.stack,
  });
  return <FallbackUI />;
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Always log errors to the console
    // eslint-disable-next-line no-console
    console.error("Error Boundary caught an error:", error, errorInfo);
  };

  const handleReset = () => {
    // Clear any error state or perform cleanup
    // This will re-render the component tree
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={handleReset}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
